import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import type { KeyDate, KeyDateType } from '@/lib/schemas'
import { api } from '@/lib/api'

// ===================
// Types
// ===================

interface KeyDatesState {
  // State
  dates: KeyDate[]
  isLoading: boolean
  error: string | null
  currentBidId: string | null

  // Filter state
  showSuperseded: boolean

  // Actions - Fetching
  fetchDates: (bidId: string) => Promise<void>

  // Actions - CRUD
  updateDate: (id: string, data: Partial<KeyDate>) => Promise<KeyDate>

  // Actions - Utility
  clearError: () => void
  reset: () => void
  setShowSuperseded: (show: boolean) => void

  // Computed
  getActiveDates: () => KeyDate[]
  getDatesByType: (type: KeyDateType) => KeyDate[]
  getUpcomingDates: () => KeyDate[]
  getPastDates: () => KeyDate[]
  getDateById: (id: string) => KeyDate | undefined
  getNextKeyDate: () => KeyDate | undefined
}

export const useKeyDatesStore = create<KeyDatesState>()(
  devtools(
    (set, get) => ({
      // Initial state
      dates: [],
      isLoading: false,
      error: null,
      currentBidId: null,
      showSuperseded: false,

      // Fetch dates for a bid
      fetchDates: async (bidId: string) => {
        try {
          set({ isLoading: true, error: null, currentBidId: bidId })
          const dates = await api.get<KeyDate[]>(`/bids/${bidId}/key-dates`)
          set({ dates, isLoading: false })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch key dates',
            isLoading: false,
          })
        }
      },

      // Update a date
      updateDate: async (id: string, data: Partial<KeyDate>) => {
        try {
          set({ error: null })
          const date = await api.patch<KeyDate>(`/key-dates/${id}`, data)
          set((state) => ({
            dates: state.dates.map((d) => (d.id === id ? date : d)),
          }))
          return date
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update key date',
          })
          throw error
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },

      // Reset store state
      reset: () => {
        set({
          dates: [],
          isLoading: false,
          error: null,
          currentBidId: null,
          showSuperseded: false,
        })
      },

      // Toggle showing superseded items
      setShowSuperseded: (show: boolean) => {
        set({ showSuperseded: show })
      },

      // Get active (non-superseded) dates
      getActiveDates: () => {
        const { dates, showSuperseded } = get()
        if (showSuperseded) return dates
        return dates.filter((d) => d.artifactStatus === 'active')
      },

      // Get dates by type
      getDatesByType: (type: KeyDateType) => {
        const activeDates = get().getActiveDates()
        return activeDates.filter((d) => d.dateType === type)
      },

      // Get upcoming dates (sorted by date)
      getUpcomingDates: () => {
        const activeDates = get().getActiveDates()
        const now = new Date()
        return activeDates
          .filter((d) => new Date(d.date) > now)
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      },

      // Get past dates (sorted by date, most recent first)
      getPastDates: () => {
        const activeDates = get().getActiveDates()
        const now = new Date()
        return activeDates
          .filter((d) => new Date(d.date) <= now)
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      },

      // Get date by ID
      getDateById: (id: string) => {
        return get().dates.find((d) => d.id === id)
      },

      // Get next upcoming key date
      getNextKeyDate: () => {
        const upcomingDates = get().getUpcomingDates()
        return upcomingDates[0]
      },
    }),
    { name: 'key-dates-store' }
  )
)
